import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faInfoCircle,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";

import { calculateScoreForSlot } from "../../utils/events/FighterScores";

import CountdownTimer from "../common/CountdownTimer";
import FighterInfoModal from "../fighter/FighterInfoModal";
import { usePrefetchFighter } from "../../hooks/fighter/useFighter";
import { useState } from "react";
import { FightDiscussionIcon } from "../threads";
import useToast from "../../hooks/useToast";

/**
 * SlotPicker - Expandable slot selection that appears when a fighter is tapped.
 * Shows a horizontal row of 5 large slot buttons with scores.
 * Color-coded based on fighter corner (red or blue).
 */
const SlotPicker = ({
  moneyline,
  lineup,
  lotn,
  alternateLotn,
  onSlotSelect,
  onCancel,
  corner,
}) => {
  const slots = [0, 1, 2, 3, 4];
  const filledSlots = lineup
    .map((f, i) => (f ? i : null))
    .filter((i) => i !== null);

  const isRed = corner === "red";
  const scoreColor = isRed ? "text-deep-red" : "text-blue-600";
  const hoverBorder = isRed
    ? "hover:border-deep-red hover:bg-deep-red/5"
    : "hover:border-blue-500 hover:bg-blue-50";
  const activeBg = isRed ? "active:bg-deep-red" : "active:bg-blue-600";
  const ringColor = isRed ? "border-deep-red/20" : "border-blue-200";

  // Get abbreviated name for occupied slot
  const getSlotOccupant = (slotIndex) => {
    const fighter = lineup[slotIndex];
    if (!fighter) return null;
    return `${fighter.FirstName.charAt(0)}. ${fighter.LastName}`;
  };

  return (
    <div className={`bg-white border-2 ${ringColor} rounded-xl p-3 mt-2`}>
      <p className="text-xs text-medium-grey font-medium text-center mb-2">
        Pick a slot to place this fighter
      </p>
      <div className="grid grid-cols-5 gap-2">
        {slots.map((slotIndex) => {
          const score = calculateScoreForSlot(moneyline, slotIndex);
          const isFilled = filledSlots.includes(slotIndex);
          const penalty = slotIndex * 20;
          const occupantName = getSlotOccupant(slotIndex);
          const isLotn =
            lineup[slotIndex] && lineup[slotIndex].FighterId === lotn;
          const isAltLotn =
            lineup[slotIndex] && lineup[slotIndex].FighterId === alternateLotn;

          return (
            <button
              key={slotIndex}
              type="button"
              onClick={() => onSlotSelect(slotIndex)}
              className={`flex flex-col items-center justify-center py-2.5 px-1 rounded-xl transition-all duration-150 cursor-pointer active:scale-95 ${
                isLotn
                  ? `bg-gradient-to-b from-gold/20 to-gold/10 border-2 border-gold ${hoverBorder} active:text-white ${activeBg}`
                  : isAltLotn
                    ? `bg-gradient-to-b from-blue-100/50 to-blue-50/30 border-2 border-blue-300 ${hoverBorder} active:text-white ${activeBg}`
                    : isFilled
                      ? `bg-gray-100 border-2 border-gray-300 ${hoverBorder} active:text-white ${activeBg}`
                      : `bg-gray-50 border-2 border-gray-200 ${hoverBorder} ${activeBg} active:text-white`
              }`}
              aria-label={`Slot ${slotIndex + 1}: ${score} pts${isLotn ? " (Lock of the Night)" : isAltLotn ? " (Alternate Lock of the Night)" : isFilled ? ` (occupied by ${occupantName})` : ""}`}
            >
              <span
                className={`text-[10px] font-bold uppercase ${isLotn ? "text-gold" : isAltLotn ? "text-blue-500" : "text-medium-grey"}`}
              >
                {isLotn ? "LOTN" : isAltLotn ? "ALT" : `Slot ${slotIndex + 1}`}
              </span>
              <span
                className={`text-lg font-bold mt-0.5 ${isLotn ? "text-gold" : isAltLotn ? "text-blue-500" : isFilled ? "text-medium-grey" : scoreColor}`}
              >
                {score}
              </span>
              {isLotn ? (
                <span className="text-[8px] text-gold font-bold truncate max-w-full leading-tight mt-0.5">
                  {occupantName}
                </span>
              ) : isAltLotn ? (
                <span className="text-[8px] text-blue-500 font-semibold truncate max-w-full leading-tight mt-0.5">
                  {occupantName}
                </span>
              ) : isFilled ? (
                <span className="text-[8px] text-deep-red font-semibold truncate max-w-full leading-tight mt-0.5">
                  {occupantName}
                </span>
              ) : (
                <span className="text-[9px] text-medium-grey">
                  {penalty === 0 ? "Full" : `-${penalty}%`}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="w-full mt-2 py-1.5 text-xs text-medium-grey hover:text-dark-grey transition-colors"
      >
        Cancel
      </button>
    </div>
  );
};

/**
 * FighterRow - A single fighter displayed as a tappable row with a colored
 * left accent border. Shows opponent name and the score for the initially
 * selected slot (the one the user clicked to open this modal).
 *
 * @param {string} corner - "red" or "blue" to indicate fighter corner
 * @param {string} opponentName - Formatted opponent name
 * @param {number} activeSlot - The slot the user selected before opening the modal
 */
const FighterRow = ({
  fighter,
  lineup,
  lotn,
  alternateLotn,
  onSlotSelect,
  isUnavailable,
  currentSlot,
  onInfoClick,
  onPrefetch,
  isExpanded,
  onExpand,
  isAlternatePick,
  onSelectAlternate,
  corner,
  opponentName,
  activeSlot,
}) => {
  const isRed = corner === "red";
  const accentBorder = isRed ? "border-l-deep-red" : "border-l-blue-500";
  const infoBg = isRed
    ? "bg-red-50 hover:bg-red-100"
    : "bg-blue-50 hover:bg-blue-100";
  const infoColor = isRed ? "text-deep-red" : "text-blue-500";
  const scoreColor = isRed ? "text-deep-red" : "text-blue-600";
  const expandedBg = isRed ? "bg-deep-red/5" : "bg-blue-50/50";

  const slotScore = calculateScoreForSlot(fighter.Moneyline, activeSlot);

  return (
    <div
      className={`border-l-4 ${accentBorder} transition-all duration-200 ${
        isExpanded ? expandedBg : ""
      }`}
    >
      {/* Main fighter row - tappable */}
      <button
        type="button"
        onClick={() => {
          if (isUnavailable) return;
          if (isAlternatePick) {
            onSelectAlternate(fighter);
          } else {
            onExpand();
          }
        }}
        disabled={isUnavailable}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-150 ${
          isUnavailable
            ? "opacity-50 cursor-not-allowed"
            : isExpanded
              ? ""
              : "hover:bg-gray-50 active:bg-gray-100 cursor-pointer"
        }`}
      >
        {/* Fighter name + status */}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-dark-grey truncate">
            {fighter.FirstName} {fighter.LastName}
          </h4>
          {currentSlot !== null && (
            <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-gold/20 text-dark-grey text-[10px] font-bold rounded-full">
              Slot {currentSlot + 1}
            </span>
          )}
          {isUnavailable && currentSlot === null && (
            <span className="inline-block mt-0.5 text-[10px] text-medium-grey italic">
              Unavailable
            </span>
          )}
        </div>

        {/* Score for the active slot (when not expanded) */}
        {!isUnavailable && !isExpanded && !isAlternatePick && (
          <div className="text-right mr-1">
            <div className={`text-sm font-bold ${scoreColor}`}>{slotScore}</div>
            <div className="text-[9px] text-medium-grey">
              Slot {activeSlot + 1} pts
            </div>
          </div>
        )}

        {/* Info button - color-coded by corner */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onInfoClick();
          }}
          onMouseEnter={onPrefetch}
          className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-colors ${infoBg}`}
          aria-label={`View ${fighter.FirstName} ${fighter.LastName} stats`}
        >
          <FontAwesomeIcon
            icon={faInfoCircle}
            className={`w-3.5 h-3.5 ${infoColor}`}
          />
        </button>

        {/* Expand indicator */}
        {!isUnavailable && !isAlternatePick && (
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="w-3 h-3 text-medium-grey"
          />
        )}
      </button>

      {/* Expanded slot picker */}
      {isExpanded && !isAlternatePick && (
        <div className="px-3 pb-3">
          <SlotPicker
            moneyline={fighter.Moneyline}
            lineup={lineup}
            lotn={lotn}
            alternateLotn={alternateLotn}
            onSlotSelect={(slotIndex) => onSlotSelect(fighter, slotIndex)}
            onCancel={onExpand}
            corner={corner}
          />
        </div>
      )}
    </div>
  );
};

/**
 * FightCard - A single bout. Two fighters stacked with a VS divider between them.
 * Each fighter has a colored left border (red / blue) to show their corner.
 */
const FightCard = ({
  fight,
  lineup,
  alternates,
  lotn,
  alternateLotn,
  onSlotSelect,
  onSelectAlternate,
  onInfoClick,
  onPrefetch,
  toast,
  isAlternatePick,
  expandedFighterId,
  onExpandFighter,
  activeSlot,
}) => {
  const fighter1 = fight.fighters[0];
  const fighter2 = fight.fighters[1];
  if (!fighter1 || !fighter2) return null;

  const formatName = (f) => `${f.FirstName.charAt(0)}. ${f.LastName}`;

  const isInLineupOrAlternates = (fighterId) => {
    return (
      lineup.some((f) => f && f.FighterId === fighterId) ||
      alternates.some((f) => f && f.FighterId === fighterId)
    );
  };

  const fighter1InLineup = isInLineupOrAlternates(fighter1.FighterId);
  const fighter2InLineup = isInLineupOrAlternates(fighter2.FighterId);

  const isUnavailable1 = fighter1InLineup || fighter2InLineup;
  const isUnavailable2 = fighter2InLineup || fighter1InLineup;

  const getSlot = (fighterId) => {
    const idx = lineup.findIndex((f) => f && f.FighterId === fighterId);
    return idx >= 0 ? idx : null;
  };

  const slot1 = getSlot(fighter1.FighterId);
  const slot2 = getSlot(fighter2.FighterId);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Matchup header - prominent fight title */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100">
        <span className="w-2 h-2 rounded-full bg-deep-red flex-shrink-0" />
        <span className="text-xs font-bold text-dark-grey truncate">
          {fighter1.FirstName} {fighter1.LastName}
        </span>
        <span className="text-[10px] text-medium-grey font-medium">vs</span>
        <span className="text-xs font-bold text-dark-grey truncate">
          {fighter2.FirstName} {fighter2.LastName}
        </span>
        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
        <div className="ml-auto">
          <FightDiscussionIcon
            fightId={fight.fightId}
            showMessageCount={true}
            size="small"
            className="opacity-70 hover:opacity-100"
            onError={(error) => {
              toast.error(`Discussion access failed: ${error.message}`);
            }}
          />
        </div>
      </div>

      {/* Fighter 1 - Red corner */}
      <FighterRow
        fighter={fighter1}
        lineup={lineup}
        lotn={lotn}
        alternateLotn={alternateLotn}
        onSlotSelect={onSlotSelect}
        onSelectAlternate={onSelectAlternate}
        isUnavailable={isUnavailable1}
        currentSlot={slot1}
        onInfoClick={() => onInfoClick(fighter1.FighterId)}
        onPrefetch={() => onPrefetch(fighter1.FighterId)}
        isExpanded={expandedFighterId === fighter1.FighterId}
        onExpand={() =>
          onExpandFighter(
            expandedFighterId === fighter1.FighterId
              ? null
              : fighter1.FighterId,
          )
        }
        isAlternatePick={isAlternatePick}
        corner="red"
        opponentName={formatName(fighter2)}
        activeSlot={activeSlot}
      />

      {/* Thin divider between fighters */}
      <div className="h-px bg-gray-100 mx-3" />

      {/* Fighter 2 - Blue corner */}
      <FighterRow
        fighter={fighter2}
        lineup={lineup}
        lotn={lotn}
        alternateLotn={alternateLotn}
        onSlotSelect={onSlotSelect}
        onSelectAlternate={onSelectAlternate}
        isUnavailable={isUnavailable2}
        currentSlot={slot2}
        onInfoClick={() => onInfoClick(fighter2.FighterId)}
        onPrefetch={() => onPrefetch(fighter2.FighterId)}
        isExpanded={expandedFighterId === fighter2.FighterId}
        onExpand={() =>
          onExpandFighter(
            expandedFighterId === fighter2.FighterId
              ? null
              : fighter2.FighterId,
          )
        }
        isAlternatePick={isAlternatePick}
        corner="blue"
        opponentName={formatName(fighter1)}
        activeSlot={activeSlot}
      />
    </div>
  );
};

/**
 * FighterSelectionModal - Modal for selecting fighters into lineup slots.
 * Tap a fighter to expand their slot picker, then tap a slot to place them.
 */
const FighterSelectionModal = ({
  position,
  onClose,
  onSelect,
  onRemoveFromSlot,
  onSwitchSlot,
  allFighters,
  lineup,
  alternates = [],
  lotn = null,
  alternateLotn = null,
  isFighterOrOpponentInLineup,
  isAlternatePick = false,
  eventStart,
}) => {
  const [infoFighterId, setInfoFighterId] = useState(null);
  const [expandedFighterId, setExpandedFighterId] = useState(null);
  const prefetchFighter = usePrefetchFighter();
  const toast = useToast();

  const processFightersData = () => {
    if (
      !allFighters ||
      !allFighters.Fights ||
      !Array.isArray(allFighters.Fights)
    ) {
      return { orderedFights: [] };
    }
    const fightsData = allFighters.Fights.filter(
      (fight) => !fight.hidden && !fight.cancelled,
    );
    const fights = [];
    fightsData.forEach((fight) => {
      if (fight && fight.Fighters && fight.Fighters.length >= 2) {
        fights.push({
          fighters: fight.Fighters,
          fightId: fight.FightId,
          order: fight.Order || 0,
        });
      }
    });
    fights.sort((a, b) => a.order - b.order);
    return { orderedFights: fights };
  };

  const { orderedFights } = processFightersData();

  const openInfo = (fighterId) => {
    setInfoFighterId(fighterId);
  };

  const closeInfo = () => setInfoFighterId(null);

  const handleSlotSelect = (fighter, slotIndex) => {
    onSelect(fighter, slotIndex);
  };

  const handleSelectAlternate = (fighter) => {
    onSelect(fighter, undefined);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-4 sm:pt-8">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal - uses dvh for proper mobile viewport sizing that accounts for
           browser chrome (address bar, bottom nav). Safe-area-inset handles notched devices. */}
      <div className="relative w-full max-w-lg mx-2 sm:mx-4 rounded-2xl shadow-2xl bg-white flex flex-col z-10 overflow-hidden fighter-selection-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-deep-red via-deep-red to-bright-red text-white p-4 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold">
                {isAlternatePick ? "Select Alternate" : "Select Your Fighter"}
              </h2>
              <p className="text-white/80 text-sm mt-0.5">
                {isAlternatePick
                  ? "Choose your alternate fighter"
                  : `Slot ${position + 1} — Tap a fighter, then pick a slot`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors -mr-1 -mt-1"
              aria-label="Close modal"
            >
              <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
            </button>
          </div>

          {/* Countdown */}
          {eventStart && (
            <div className="mt-2">
              <CountdownTimer
                targetDate={eventStart}
                eventName={allFighters?.Name || allFighters?.name}
                className="bg-white/20 text-white px-3 py-1"
                showIcon={true}
              />
            </div>
          )}
        </div>

        {/* Fighter List */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain p-2 sm:p-3 bg-gray-50"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {orderedFights.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <p className="text-medium-grey">
                No fighters available to display
              </p>
            </div>
          ) : (
            <div className="space-y-3 pb-6">
              {orderedFights.map((fight, idx) => (
                <FightCard
                  key={idx}
                  fight={fight}
                  lineup={lineup}
                  alternates={alternates}
                  lotn={lotn}
                  alternateLotn={alternateLotn}
                  onSlotSelect={handleSlotSelect}
                  onSelectAlternate={handleSelectAlternate}
                  onInfoClick={openInfo}
                  onPrefetch={(fighterId) => prefetchFighter(fighterId)}
                  toast={toast}
                  isAlternatePick={isAlternatePick}
                  expandedFighterId={expandedFighterId}
                  onExpandFighter={setExpandedFighterId}
                  activeSlot={position}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fighter Info Modal */}
      {infoFighterId && (
        <FighterInfoModal fighterId={infoFighterId} onClose={closeInfo} />
      )}
    </div>
  );
};

export default FighterSelectionModal;
